/**
 * MainSettingsPage - Modern card-based hub for all settings
 * Design matches PortalSettingsPage style
 */
import React from 'react';
import { 
  Settings, Building2, Calendar, Clock, CreditCard, Users, 
  MessageCircle, Globe, Package, FileText, Shield, Database,
  Zap, Percent, Euro, Plug, Palette, ClipboardList,
  Tag, Receipt, RefreshCw, Sparkles, Smartphone, Utensils, Scan,
  Mail, Gift, Timer, Star, Bell, BarChart3
} from 'lucide-react';
import { Camera } from 'lucide-react';
import { Megaphone } from 'lucide-react';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useTenantBranding } from '@/contexts/TenantBrandingContext';

export const settingsCategories = [
    {
      title: 'Studio & Allgemein',
      cards: [
        {
          id: 'general',
          icon: Building2,
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          title: 'Studio-Informationen',
          description: 'Name, Adresse, Kontaktdaten',
          path: '/settings/advanced?tab=general'
        },
        {
          id: 'studios',
          icon: Building2,
          bgColor: 'bg-sky-100',
          iconColor: 'text-sky-600',
          title: 'Studio-Verwaltung',
          description: 'Mehrere Studios / Standorte verwalten',
          path: '/studios'
        },
        {
          id: 'policies',
          icon: Shield,
          bgColor: 'bg-indigo-100',
          iconColor: 'text-indigo-600',
          title: 'Policies',
          description: 'Datenschutz & AGB',
          path: '/settings/advanced?tab=policies'
        },
        {
          id: 'security',
          icon: Shield,
          bgColor: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          title: 'Sicherheit & 2FA',
          description: 'Zwei-Faktor-Login & 2FA-Pflicht für Mitarbeitende',
          path: '/settings/security'
        },
        {
          id: 'anamnese',
          icon: ClipboardList,
          bgColor: 'bg-indigo-100',
          iconColor: 'text-indigo-600',
          title: 'Anamnese',
          description: 'Fragebogen für Neukunden',
          path: '/settings/advanced?tab=anamnese'
        },
        {
          id: 'holidays',
          icon: Calendar,
          bgColor: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'Feiertage & Ferien',
          description: 'Erinnerungen vor Feiertagen & Schulferien',
          path: '/settings/holidays'
        },
        {
          id: 'availability',
          icon: Calendar,
          bgColor: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'Öffnungszeiten & Kalender',
          description: 'Kalender-Anzeige & Farben: wie der Kalender dargestellt & geöffnet ist (nicht die buchbaren Slots)',
          path: '/settings/advanced?tab=availability'
        },
        {
          id: 'calendar-menu',
          icon: Calendar,
          bgColor: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'Termin-Menü (Rechtsklick)',
          description: 'Welche Aktionen das Rechtsklick-Menü im Kalender zeigt & in welcher Reihenfolge',
          path: '/settings/calendar-menu'
        },
        {
          id: 'calendar-columns',
          icon: Calendar,
          bgColor: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'Kalenderspalten',
          description: 'Reihenfolge der Kalenderspalten per Drag-&-Drop ordnen & einzelne Spalten pro Betrieb aus-/einblenden',
          path: '/settings/calendar-columns'
        },
        {
          id: 'custom-fields',
          icon: ClipboardList,
          bgColor: 'bg-indigo-100',
          iconColor: 'text-indigo-600',
          title: 'Eigene Kundenfelder',
          description: 'Frei definierbare Kundenfelder (Anzugsgrösse, Schuhgrösse, Hinweise …) & wo sie sichtbar sind',
          path: '/settings/custom-fields'
        },
        {
          id: 'onboarding-builder',
          icon: ClipboardList,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'Onboarding-Baukasten',
          description: 'Ablauf für neue Kunden per Drag-&-Drop zusammenstellen (Schritte & eigene Felder)',
          path: '/settings/onboarding-builder'
        },
        {
          id: 'modules',
          icon: ClipboardList,
          bgColor: 'bg-sky-100',
          iconColor: 'text-sky-600',
          title: 'Module',
          description: 'Funktionen ein-/ausschalten — wirkt in Admin, Kundenportal und App',
          path: '/settings/modules'
        },
        {
          id: 'menu-layout',
          icon: ClipboardList,
          bgColor: 'bg-teal-100',
          iconColor: 'text-teal-600',
          title: 'Menü & Navigation',
          description: 'Reihenfolge & Sichtbarkeit der Bereiche in der Seitenleiste — team-weit und pro Mitarbeiter',
          path: '/settings/menu-layout'
        }
      ]
    },
    {
      title: 'Service & Produkte',
      cards: [
        {
          id: 'categories',
          icon: Tag,
          bgColor: 'bg-yellow-100',
          iconColor: 'text-yellow-600',
          title: 'Kategorien',
          description: 'Service-Kategorien verwalten',
          path: '/settings/advanced?tab=categories'
        },
        {
          id: 'services',
          icon: Package,
          bgColor: 'bg-teal-100',
          iconColor: 'text-teal-600',
          title: 'Dienstleistungen',
          description: 'Services, Kurse und Preise',
          path: '/services'
        },
        {
          id: 'addons',
          icon: Plug,
          bgColor: 'bg-fuchsia-100',
          iconColor: 'text-fuchsia-600',
          title: 'Zusatzleistungen',
          description: 'Add-on Services nach Buchung',
          path: '/settings/advanced?tab=addons'
        },
        {
          id: 'products',
          icon: Package,
          bgColor: 'bg-orange-100',
          iconColor: 'text-orange-600',
          title: 'Pakete & Produkte',
          description: 'Verkaufsprodukte verwalten',
          path: '/products'
        },
        {
          id: 'resources',
          icon: Building2,
          bgColor: 'bg-sky-100',
          iconColor: 'text-sky-600',
          title: 'Ressourcen',
          description: 'Räume, EMS-Anzüge, Geräte',
          path: '/resources'
        }
      ]
    },
    {
      title: 'Finanzen & Steuern',
      cards: [
        {
          id: 'tax',
          icon: Percent,
          bgColor: 'bg-red-100',
          iconColor: 'text-red-600',
          title: 'MWST-Einstellungen',
          description: 'Steuersätze und Preisanzeige',
          path: '/settings/advanced?tab=tax'
        },
        {
          id: 'invoice',
          icon: Receipt,
          bgColor: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          title: 'Rechnungs- & Firmendaten',
          description: 'Firmeninfos & Zahlungsziel für Rechnungen',
          path: '/settings/advanced?tab=invoice'
        },
        {
          id: 'billing-auto',
          icon: RefreshCw,
          bgColor: 'bg-cyan-100',
          iconColor: 'text-cyan-600',
          title: 'Automatische Abrechnung',
          description: 'Abo-Abrechnung & Mahnungen',
          path: '/settings/advanced?tab=billing-auto'
        },
        {
          id: 'bundle-reminders',
          icon: RefreshCw,
          bgColor: 'bg-cyan-100',
          iconColor: 'text-cyan-600',
          title: 'Sammel-Zahlungsaufforderung',
          description: 'Mehrere offene Trainings auf einmal einfordern',
          path: '/settings/bundle-reminders'
        },
        {
          id: 'scheduler',
          icon: Zap,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'Zeitplaner',
          description: 'Automatische Prozesse & Zeitpläne (gehört zur automatischen Abrechnung)',
          path: '/settings/advanced?tab=scheduler'
        },
        {
          id: 'payment-methods',
          icon: CreditCard,
          bgColor: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          title: 'Zahlungsarten (Vor Ort)',
          description: 'Bar, TWINT, SumUp … + ERP-Zuordnung',
          path: '/settings/payment-methods'
        },
        {
          id: 'sumup',
          icon: CreditCard,
          bgColor: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'SumUp Kartenleser (Solo)',
          description: 'Physische Kartenzahlung am Terminal – Keys & Leser koppeln',
          path: '/settings/sumup'
        },
        {
          id: 'zahlungen',
          icon: CreditCard,
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          title: 'Online-Zahlungen (Payrexx)',
          description: 'Payrexx Zahlungs-Integration',
          path: '/settings/advanced?tab=zahlungen'
        },
        {
          id: 'epost',
          icon: Mail,
          bgColor: 'bg-red-100',
          iconColor: 'text-red-600',
          title: 'ePost Briefversand',
          description: 'Rechnungen automatisch per Post versenden',
          path: '/settings/epost'
        },
        {
          id: 'tarif595',
          icon: ClipboardList,
          bgColor: 'bg-teal-100',
          iconColor: 'text-teal-600',
          title: 'Tarif 595',
          description: 'Krankenkassen-Abrechnungen, GLN/ZSR',
          path: '/settings/advanced?tab=tarif595'
        },
        {
          id: 'physio-billing',
          icon: Receipt,
          bgColor: 'bg-teal-100',
          iconColor: 'text-teal-600',
          title: 'Physio-Abrechnung (Tarif 311/590)',
          description: 'Kassenrechnungen Physiotherapie – Kostenträger, Tarife, Taxpunkte, Praxis-GLN/ZSR',
          path: '/settings/physio-billing'
        }
      ]
    },
    {
      title: 'Kommunikation',
      cards: [
        {
          id: 'kanaele',
          icon: MessageCircle,
          bgColor: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          title: 'Kanäle',
          description: 'E-Mail (SMTP), WhatsApp API & Push (Firebase)',
          path: '/settings/advanced?tab=kanaele'
        },
        {
          id: 'communication',
          icon: Clock,
          bgColor: 'bg-indigo-100',
          iconColor: 'text-indigo-600',
          title: 'Erinnerungen & Vorlagen',
          description: 'Terminerinnerungen, E-Mail-/WhatsApp-Vorlagen, Geburtstage, Newsletter',
          path: '/settings/advanced?tab=communication'
        },
        {
          id: 'termin-feedback',
          icon: Star,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'Termin-Feedback',
          description: 'Wie oft Kunden nach dem Training um Feedback gebeten werden (Standard: max. alle 60 Tage)',
          path: '/settings/advanced?tab=termin-feedback'
        },
        {
          id: 'automation',
          icon: Zap,
          bgColor: 'bg-orange-100',
          iconColor: 'text-orange-600',
          title: 'Automatisierung',
          description: 'Automatische Benachrichtigungs-Regeln',
          path: '/automation/rules'
        },
        {
          id: 'reengagement',
          icon: RefreshCw,
          bgColor: 'bg-cyan-100',
          iconColor: 'text-cyan-600',
          title: 'Re-Engagement',
          description: 'Inaktive Kunden erinnern & Rabattcodes',
          path: '/settings/re-engagement'
        },
        {
          id: 'push_configs',
          icon: Bell,
          bgColor: 'bg-yellow-100',
          iconColor: 'text-yellow-600',
          title: 'Push-Texte',
          description: 'Termin-Erinnerungen, Inaktivität & Smart-Coach',
          path: '/push/configs'
        },
        {
          id: 'email_templates',
          icon: FileText,
          bgColor: 'bg-teal-100',
          iconColor: 'text-teal-600',
          title: 'E-Mail-Vorlagen',
          description: 'E-Mail-Templates bearbeiten',
          path: '/email/templates'
        },
        {
          id: 'whatsapp_templates',
          icon: FileText,
          bgColor: 'bg-green-100',
          iconColor: 'text-green-600',
          title: 'WhatsApp Templates',
          description: 'Templates für Meta',
          path: '/whatsapp/templates'
        },
        {
          id: 'card_expiry',
          icon: CreditCard,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'Kreditkarten-Ablauf',
          description: 'Benachrichtigungen wenn Karten ablaufen',
          path: '/settings/card-expiry'
        },
        {
          id: 'logs',
          icon: BarChart3,
          bgColor: 'bg-gray-100',
          iconColor: 'text-gray-600',
          title: 'Versand-Logs',
          description: 'E-Mail-Historie & Versandprotokoll',
          path: '/email/logs'
        },
        {
          id: 'report-email',
          icon: Mail,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'Report-E-Mail',
          description: 'KI-Performance & Website-Analytics per E-Mail',
          path: '/settings/advanced?card=report-email',
          testId: 'report-email-settings-card-main'
        },
        {
          id: 'trial-followup',
          icon: Gift,
          bgColor: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          title: 'Probetraining Follow-Up',
          description: '2. Probetraining gratis bei anderem Trainer',
          path: '/settings/advanced?card=trial-followup'
        },
        {
          id: 'email-diagnostics',
          icon: Mail,
          bgColor: 'bg-orange-100',
          iconColor: 'text-orange-600',
          title: 'E-Mail-Diagnose',
          description: 'Wo ist diese E-Mail-Adresse gespeichert?',
          path: '/settings/email-diagnostics'
        }
      ]
    },
    {
      title: 'Marketing',
      cards: [
        {
          id: 'google-review',
          icon: Star,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'Google-Bewertung',
          description: 'Automatische WhatsApp-Bewertungsanfrage (Link je Studio in der Studio-Verwaltung)',
          path: '/settings/advanced?tab=google-review'
        },
        {
          id: 'referral-sources',
          icon: Megaphone,
          bgColor: 'bg-pink-100',
          iconColor: 'text-pink-600',
          title: 'Marketing-Quellen',
          description: 'Optionen für „Wie hast du von uns erfahren?" verwalten',
          path: '/settings/referral-sources'
        },
        {
          id: 'slot-reservation',
          icon: Timer,
          bgColor: 'bg-yellow-100',
          iconColor: 'text-yellow-700',
          title: 'Chatbot Slot-Reservation',
          description: 'Wie lange werden vorgeschlagene Termine geblockt? (Default 3h)',
          path: '/settings/advanced?card=slot-reservation',
          testId: 'slot-reservation-settings-card-main'
        },
        {
          id: 'google-business-hours',
          icon: Globe,
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          title: 'Google Öffnungszeiten',
          description: 'Öffnungszeiten bei Google automatisch aus dem Kalender pflegen',
          path: '/admin/integrations/google-business'
        }
      ]
    },
    {
      title: 'KI',
      cards: [
        {
          id: 'ki',
          icon: Sparkles,
          bgColor: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'KI',
          description: 'KI-Provider, Modelle, WhatsApp-Bot & Ernährungs-KI',
          path: '/settings/advanced?tab=ki'
        }
      ]
    },
    {
      title: 'Kundenportal & App',
      cards: [
        {
          id: 'portal',
          icon: Globe,
          bgColor: 'bg-green-100',
          iconColor: 'text-green-600',
          title: 'Kundenportal',
          description: 'Aussehen, Funktionen, Einbettung',
          path: '/portal-settings'
        }
      ]
    },
    {
      title: 'Team & Zugriff',
      cards: [
        {
          id: 'trainers',
          icon: Users,
          bgColor: 'bg-rose-100',
          iconColor: 'text-rose-600',
          title: 'Personal',
          description: 'Trainer & Mitarbeiter verwalten',
          path: '/personal'
        },
        {
          id: 'timetracking',
          icon: Clock,
          bgColor: 'bg-teal-100',
          iconColor: 'text-teal-600',
          title: 'Stundenerfassung',
          description: 'Arbeitszeiten, Ferien & Abwesenheiten',
          path: '/personal?tab=stunden'
        },
        {
          id: 'payroll',
          icon: CreditCard,
          bgColor: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          title: 'Lohn & AHV',
          description: 'Lohnabrechnung, AHV-Nummer & Bonus',
          path: '/personal?tab=lohn'
        },
        {
          id: 'studio-bonus',
          icon: Percent,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'Standort-Bonus (GF)',
          description: 'Umsatzabhängiger Geschäftsführer-Bonus je Studio',
          path: '/studios/bonus'
        }
      ]
    },
    {
      title: 'Integrationen',
      cards: [
        {
          id: 'erp',
          icon: Plug,
          bgColor: 'bg-cyan-100',
          iconColor: 'text-cyan-600',
          title: 'Buchhaltung / ERP',
          description: 'API-Zugang & automatischer Rechnungs-Sync',
          path: '/settings/erp'
        },
        {
          id: 'mindbody-integration',
          icon: Plug,
          bgColor: 'bg-rose-100',
          iconColor: 'text-rose-600',
          title: 'Mindbody Integration',
          description: 'Read-Only Mirror der Mindbody-Termine (Migrations-Phase)',
          path: '/admin/integrations/mindbody'
        }
      ]
    },
    {
      title: 'System',
      cards: [
        {
          id: 'mobile-app',
          icon: Smartphone,
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          title: 'Mobile App',
          description: 'App-Preview, Branding & Notifications',
          path: '/mobile-app'
        },
        {
          id: 'density-learning',
          icon: Sparkles,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'KI-Lern-Statistik (LiDAR)',
          description: 'Was hat die KI aus Wiegungen gelernt?',
          path: '/admin/density-learning'
        },
        {
          id: 'nutrition',
          icon: Utensils,
          bgColor: 'bg-orange-100',
          iconColor: 'text-orange-600',
          title: 'Ernährung',
          description: 'Standard-Mahlzeiten-Verteilung',
          path: '/settings/advanced?tab=nutrition'
        },
        {
          id: 'scanner',
          icon: Camera,
          bgColor: 'bg-cyan-100',
          iconColor: 'text-cyan-600',
          title: 'Foto-Scanner',
          description: 'Tiefen-Capture (LiDAR) für Essens-Fotos',
          path: '/settings/advanced?card=scanner'
        },
        {
          id: 'inbody',
          icon: Scan,
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          title: 'InBody',
          description: 'Körperanalyse für Kunden aktivieren',
          path: '/settings/advanced?tab=inbody'
        },
        {
          id: 'maintenance',
          icon: Clock,
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          title: 'Wartung',
          description: 'Termin-Zeitzonen normalisieren',
          path: '/settings/advanced?card=maintenance',
          testId: 'maintenance-settings-card-main'
        },
        {
          id: 'backup',
          icon: Database,
          bgColor: 'bg-green-100',
          iconColor: 'text-green-600',
          title: 'Backup & Export',
          description: 'Automatische Datensicherung',
          path: '/settings/advanced?tab=backup'
        }
      ]
    }
  ];

const MainSettingsPage = () => {
  const navigate = useNavigate();
  const { companyName, name: brandName } = useTenantBranding();
  const businessName = companyName || brandName || 'Ihr Betrieb';

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="h-6 w-6 text-gray-600" />
          Einstellungen
        </h1>
        <p className="text-gray-600 mt-1">
          Konfigurieren Sie alle Aspekte Ihres Fitness-Studios
        </p>
      </div>

      <div className="space-y-10">
        {settingsCategories.map(category => (
          <div key={category.title}>
            <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              {category.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {category.cards.map(card => {
                const Icon = card.icon;
                return (
                  <Card
                    key={card.id}
                    className={`cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] ${card.danger ? 'border-2 border-red-200 hover:bg-red-50' : 'border border-gray-100'}`}
                    onClick={() => navigate(card.path)}
                    data-testid={card.testId || `${card.id}-settings-card-main`}
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className={`flex items-center gap-3 text-base ${card.danger ? 'text-red-700' : ''}`}>
                        <div className={`p-2.5 rounded-lg ${card.bgColor}`}>
                          <Icon className={`h-5 w-5 ${card.iconColor}`} />
                        </div>
                        <span className="font-medium">{card.title}</span>
                      </CardTitle>
                      <CardDescription className={`text-xs mt-1 ml-12 ${card.danger ? 'text-red-600' : ''}`}>
                        {card.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Version Info */}
      <div className="mt-12 pt-6 border-t text-center text-sm text-gray-400">
        <p>{businessName} v2.1.0</p>
      </div>
    </div>
  );
};

export default MainSettingsPage;
